import { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import EditModal from 'components/UI/EditModal';
import Button from 'components/UI/Button';
import { Content, ButtonGrid } from './styled';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useAppDispatch } from 'hooks/redux';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import { cancelRollout } from 'store/rolloutState/rolloutState';
import { TableRollout } from '../RolloutTable';
import { useAccess } from 'hooks/useAccess';
import InputField from 'components/UI/InputField';

type Props = {
  rollout: TableRollout | undefined;
  open: boolean;
  onClose: () => void;
};

function manyDevicesToCancel(rollout: TableRollout | undefined): boolean {
  if (!rollout?.progress.waiting) {
    return false;
  }
  return rollout.progress.waiting > 300;
}

const RolloutCancelConfirmation: FC<Props> = ({ rollout, open, onClose }) => {
  const dispatch = useAppDispatch();
  const { adminAccess } = useAccess();
  const { handleSubmit, register } = useForm();

  // Submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values: FieldValues) => {
      if (rollout) {
        dispatch(cancelRollout(rollout.link, values.offset | 0));
        onClose();
      }
    },
    [dispatch, rollout, onClose]
  );

  const renderText = useCallback(
    (rollout: TableRollout | undefined) => {
      if (!rollout) {
        return null;
      }
      if (manyDevicesToCancel(rollout)) {
        if (adminAccess) {
          return (
            <div>
              <FormattedMessage
                id="rollout.confirm_cancel_admin_text"
                values={{
                  numbDevices: rollout?.progress.waiting,
                  rollout: rollout?.name,
                }}
              />
              <InputField
                description="rollout.offset"
                defaultValue="0"
                register={register('offset')}
                disabled={false}
              />
            </div>
          );
        }
        return (
          <FormattedMessage id="rollout.confirm_cancel_not_possible_text" />
        );
      }
      return (
        <FormattedMessage
          id="rollout.confirm_cancel_text"
          values={{
            numbDevices: rollout?.progress.waiting,
            software: rollout?.software,
            rollout: rollout?.name,
          }}
        />
      );
    },
    [adminAccess, register]
  );

  return (
    <EditModal title="rollout.confirm_cancel_title" open={open} close={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Content>
          <Heading type={HeadingType.h4}>{renderText(rollout)}</Heading>
        </Content>
        <ButtonGrid>
          <Button
            onClick={onClose}
            backgroundColor="transparent"
            color="primary"
          >
            <FormattedMessage id="rollout.btn_cancel" />
          </Button>
          <Button
            submit
            disabled={manyDevicesToCancel(rollout) && !adminAccess}
          >
            <FormattedMessage id="rollout.btn_confirm" />
          </Button>
        </ButtonGrid>
      </form>
    </EditModal>
  );
};

export default RolloutCancelConfirmation;
